import { fromMarkdown } from "mdast-util-from-markdown";
import { gfmFromMarkdown } from "mdast-util-gfm";
import { gfm } from "micromark-extension-gfm";
import { helloSchema, incomingServerMessageSchema, profileSchema, serverErrorFrameSchema, signalEnvelopeSchema, signalTextSchema } from "@openmeld/schemas/schemas";
//#region src/constants.ts
const MAX_PROFILE_ID_LENGTH = 64;
const MAX_PROFILE_NAME_LENGTH = 64;
const MAX_TEXT_LENGTH = 1e5;
const SPACE_STREAM_KEEPALIVE_PING = "ping";
const SPACE_STREAM_KEEPALIVE_PONG = "pong";
const DAEMON_STREAM_KEEPALIVE_PING = "ping";
const DAEMON_STREAM_KEEPALIVE_PONG = "pong";
//#endregion
//#region src/message-links.ts
const LINK_HINT = /https?:\/\/|www\.|mailto:|@/i;
const BARE_WEB_LINK = /^(?:https?:\/\/|www\.)/i;
const TRAILING_CJK_PUNCTUATION = /[。，、；：！？…]+$/u;
/** Shared by plain-text presentation, Markdown anchors, and server previews. */
function normalizeMessageLink(value) {
	try {
		const url = new URL(value);
		if (![
			"https:",
			"http:",
			"mailto:"
		].includes(url.protocol) || url.username || url.password) return null;
		return url.href;
	} catch {
		return null;
	}
}
function nodeText(node) {
	return node.value ?? node.children?.map(nodeText).join("") ?? "";
}
function readLink(node, text) {
	let value = node.url ?? "";
	const start = node.position?.start.offset;
	let end = node.position?.end.offset;
	let label = nodeText(node);
	if (start === void 0 || end === void 0) return null;
	if (BARE_WEB_LINK.test(text.slice(start, end))) {
		const trimmed = label.replace(TRAILING_CJK_PUNCTUATION, "");
		end -= label.length - trimmed.length;
		label = trimmed;
		value = trimmed.startsWith("www.") ? `http://${trimmed}` : trimmed;
	}
	const url = normalizeMessageLink(value);
	return url ? {
		start,
		end,
		url,
		label: label || url
	} : null;
}
function collectMessageLinks(text) {
	if (!LINK_HINT.test(text)) return [];
	const tree = fromMarkdown(text, {
		extensions: [gfm()],
		mdastExtensions: [gfmFromMarkdown()]
	});
	const links = [];
	function visit(node) {
		if ([
			"code",
			"inlineCode",
			"html",
			"image",
			"imageReference"
		].includes(node.type)) return;
		if (node.type === "link") {
			const link = readLink(node, text);
			if (link) links.push(link);
			return;
		}
		for (const child of node.children ?? []) visit(child);
	}
	visit(tree);
	return links;
}
function collectMessagePreviewUrls(text) {
	return [...new Set(collectMessageLinks(text).map((link) => link.url).filter((url) => url.startsWith("https://") || url.startsWith("http://")))];
}
//#endregion
//#region src/parse.ts
function parseJsonObject(value) {
	try {
		const parsed = JSON.parse(value);
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {
			ok: false,
			error: "invalid_json"
		};
		return {
			ok: true,
			value: parsed
		};
	} catch {
		return {
			ok: false,
			error: "invalid_json"
		};
	}
}
function parseProfile(value) {
	const parsed = profileSchema.safeParse(value);
	if (parsed.success) return {
		ok: true,
		value: parsed.data
	};
	return {
		ok: false,
		error: parsed.error.issues.some((issue) => issue.path[0] === "profileId") ? "invalid_profile_id" : "invalid_profile"
	};
}
function parseSpaceCreator(value) {
	if (!value || typeof value !== "object") return null;
	return parseSpaceCreatorFromCreatorShape(value) ?? parseSpaceCreatorFromProfileShape(value);
}
function normalizeProfileId(value) {
	const profile = parseProfile({ profileId: value });
	if (!profile.ok) return null;
	return profile.value.profileId;
}
function parseHello(obj) {
	if (obj.type !== "hello") return {
		ok: false,
		error: "not_hello"
	};
	const profile = parseProfile(obj.profile);
	if (!profile.ok) return {
		ok: false,
		error: profile.error
	};
	const parsed = helloSchema.safeParse({
		type: "hello",
		profile: profile.value,
		...obj.capabilities === void 0 ? {} : { capabilities: obj.capabilities }
	});
	if (!parsed.success) return {
		ok: false,
		error: "invalid_profile"
	};
	return {
		ok: true,
		value: parsed.data
	};
}
function parseSignalText(obj) {
	if (obj.type !== "signal.text") return {
		ok: false,
		error: "not_signal_text"
	};
	const data = obj.data;
	if (!data || typeof data !== "object") return {
		ok: false,
		error: "invalid_signal_data"
	};
	if (Array.isArray(data)) return {
		ok: false,
		error: "invalid_signal_data"
	};
	const parsed = signalTextSchema.safeParse(obj);
	if (parsed.success) return {
		ok: true,
		value: parsed.data
	};
	if (parsed.error.issues.some((issue) => issue.path[0] === "data" && issue.path[1] === "text" && issue.code === "too_big")) return {
		ok: false,
		error: "text_too_long"
	};
	return {
		ok: false,
		error: "empty_text"
	};
}
function parseEnvelope(value) {
	const parsed = signalEnvelopeSchema.safeParse(value);
	return parsed.success ? parsed.data : null;
}
function parseIncoming(value) {
	const parsed = incomingServerMessageSchema.safeParse(value);
	return parsed.success ? parsed.data : null;
}
function parseServerErrorFrame(value) {
	const parsed = serverErrorFrameSchema.safeParse(value);
	return parsed.success ? parsed.data : null;
}
function parseSpaceCreatorFromCreatorShape(value) {
	const creator = value;
	if (typeof creator.profileId !== "string") return null;
	const profileId = creator.profileId.trim();
	if (!profileId || profileId.length > 64) return null;
	const profileName = typeof creator.profileName === "string" && creator.profileName.trim().length > 0 ? creator.profileName.trim() : void 0;
	const kind = creator.kind === "human" || creator.kind === "agent" || creator.kind === "system" ? creator.kind : void 0;
	const profile = typeof creator.profile === "string" && creator.profile.trim().length > 0 ? creator.profile.trim() : void 0;
	const ownerUserIdRaw = creator.ownerUserId ?? creator.owner;
	const ownerUserId = typeof ownerUserIdRaw === "string" && ownerUserIdRaw.trim().length > 0 ? ownerUserIdRaw.trim().slice(0, 64) : void 0;
	return {
		profileId,
		...profileName ? { profileName } : {},
		...kind ? { kind } : {},
		...profile ? { profile } : {},
		...ownerUserId ? { ownerUserId } : {}
	};
}
function parseSpaceCreatorFromProfileShape(value) {
	const parsedProfile = parseProfile(value);
	if (!parsedProfile.ok) return null;
	const profileRaw = value.profile;
	const profile = typeof profileRaw === "string" && profileRaw.trim().length > 0 ? profileRaw.trim() : void 0;
	return {
		profileId: parsedProfile.value.profileId,
		...parsedProfile.value.profileName ? { profileName: parsedProfile.value.profileName } : {},
		...parsedProfile.value.kind ? { kind: parsedProfile.value.kind } : {},
		...profile ? { profile } : {},
		...parsedProfile.value.owner ? { ownerUserId: parsedProfile.value.owner.trim().slice(0, 64) } : {}
	};
}
//#endregion
//#region src/profile-display.ts
function buildProfileDisplay(input) {
	const profileId = normalizeOptionalText(input.openMeldProfileId ?? input.profileId);
	if (!profileId) throw new TypeError("profile display requires openMeldProfileId");
	const profileName = normalizeOptionalText(input.profileName);
	const ownerUserId = normalizeOptionalText(input.ownerUserId);
	const ownerName = normalizeOptionalText(input.ownerName);
	const agentControllerRef = resolveAgentControllerRef(input);
	const agentControllerConversationId = normalizeOptionalText(input.agentControllerConversationId);
	return {
		kind: input.profileKind,
		profileId,
		...profileName ? { profileName } : {},
		...input.profileKind === "agent" && ownerUserId ? { owner: ownerUserId } : {},
		...input.profileKind === "agent" && ownerName ? { ownerName } : {},
		...input.profileKind === "agent" && agentControllerRef ? { agentControllerRef } : {},
		...input.profileKind === "agent" && agentControllerConversationId ? { agentControllerConversationId } : {}
	};
}
function resolveAgentControllerRef(input) {
	return normalizeOptionalText(input.agentControllerRef);
}
function normalizeOptionalText(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
//#endregion
//#region src/profile-handle.ts
const MAX_MENTION_TOKEN_LENGTH = 128;
const MENTION_TOKEN_REGEX = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const HANDLE_SEED_SANITIZE_REGEX = /[^a-z0-9]+/g;
const HANDLE_SEED_MULTI_SEPARATOR_REGEX = /-+/g;
const HANDLE_SEED_EDGE_SEPARATOR_REGEX = /^-+|-+$/g;
const LEGACY_MENTION_ALIAS_SEED_MAX_LENGTH = 96;
const LEGACY_MENTION_ALIAS_PROFILE_ID_SUFFIX_LENGTH = 8;
const LEGACY_HANDLE_SEED_START_CHAR_REGEX = /^[a-z0-9]/;
const LEGACY_HANDLE_SEED_SANITIZE_REGEX = /[^A-Za-z0-9._-]+/g;
const LEGACY_HANDLE_SEED_MULTI_SEPARATOR_REGEX = /[._-]{2,}/g;
const LEGACY_HANDLE_SEED_EDGE_SEPARATOR_REGEX = /^[._-]+|[._-]+$/g;
const PROFILE_ID_SUFFIX_SANITIZE_REGEX = /[^a-z0-9]/g;
function normalizeMentionToken(value) {
	if (typeof value !== "string") return null;
	const normalized = value.trim();
	if (normalized.length === 0 || normalized.length > 128 || !MENTION_TOKEN_REGEX.test(normalized)) return null;
	return normalized;
}
function buildProfileHandleBase(input) {
	const bounded = input.profileName.trim().toLowerCase().replace(HANDLE_SEED_SANITIZE_REGEX, "-").replace(HANDLE_SEED_MULTI_SEPARATOR_REGEX, "-").replace(HANDLE_SEED_EDGE_SEPARATOR_REGEX, "").slice(0, 128);
	if (bounded.length > 0) return bounded;
	return resolveProfileHandleFallbackBase(input.profileKind);
}
function buildProfileHandleCandidate(input) {
	const sequence = Math.max(1, Math.trunc(input.sequence));
	const normalizedBase = normalizeMentionToken(input.baseHandle) ?? normalizeMentionToken(resolveProfileHandleFallbackBase(input.profileKind)) ?? "profile";
	if (sequence === 1) return normalizedBase;
	const suffix = `-${sequence}`;
	const boundedBase = normalizedBase.slice(0, 128 - suffix.length).replace(HANDLE_SEED_EDGE_SEPARATOR_REGEX, "");
	const fallbackBase = resolveProfileHandleFallbackBase(input.profileKind).slice(0, Math.max(1, 128 - suffix.length));
	return `${boundedBase || fallbackBase}${suffix}`;
}
function buildProfileHandleCandidates(input) {
	const baseHandle = buildProfileHandleBase(input);
	const limit = Math.max(1, Math.trunc(input.limit ?? 8));
	const candidates = [];
	for (let sequence = 1; sequence <= limit; sequence += 1) candidates.push(buildProfileHandleCandidate({
		baseHandle,
		profileKind: input.profileKind,
		sequence
	}));
	return candidates;
}
function resolveAvailableProfileHandle(input) {
	const takenHandles = /* @__PURE__ */ new Set();
	for (const handle of input.takenHandles ?? []) {
		const normalized = normalizeMentionToken(handle);
		if (normalized) takenHandles.add(normalized.toLowerCase());
	}
	const baseHandle = buildProfileHandleBase(input);
	for (let sequence = 1; sequence < Number.MAX_SAFE_INTEGER; sequence += 1) {
		const candidate = buildProfileHandleCandidate({
			baseHandle,
			profileKind: input.profileKind,
			sequence
		});
		if (!takenHandles.has(candidate.toLowerCase())) return candidate;
	}
	throw new Error("failed to resolve available profile handle");
}
function buildLegacyDefaultMentionAliasCandidates(input) {
	const aliases = [];
	const normalizedProfileName = normalizeMentionToken(input.profileName);
	if (normalizedProfileName) aliases.push(normalizedProfileName);
	const profileSeed = buildLegacyDefaultMentionAliasSeed({
		profileName: input.profileName,
		profileKind: input.profileKind
	});
	const profileIdSuffix = normalizeLegacyProfileIdSuffix(input.profileId);
	const fallbackAlias = normalizeMentionToken(profileIdSuffix ? `${profileSeed}-${profileIdSuffix}` : profileSeed);
	if (fallbackAlias && !aliases.includes(fallbackAlias)) aliases.push(fallbackAlias);
	return aliases;
}
function isLegacyDefaultMentionAlias(input) {
	const mentionAlias = normalizeMentionToken(input.mentionAlias);
	if (!mentionAlias) return false;
	const profileName = typeof input.profileName === "string" ? input.profileName : null;
	const profileId = typeof input.profileId === "string" ? input.profileId : null;
	const profileKind = input.profileKind ?? null;
	if (!(profileName && profileId && profileKind)) return false;
	return buildLegacyDefaultMentionAliasCandidates({
		profileName,
		profileId,
		profileKind
	}).some((candidate) => candidate.toLowerCase() === mentionAlias.toLowerCase());
}
function resolveCustomMentionAliasOverride(input) {
	const mentionAlias = normalizeMentionToken(input.mentionAlias);
	if (!mentionAlias) return null;
	return isLegacyDefaultMentionAlias({
		mentionAlias,
		profileName: input.profileName,
		profileId: input.profileId,
		profileKind: input.profileKind
	}) ? null : mentionAlias;
}
function resolveProfileHandleFallbackBase(profileKind) {
	switch (profileKind) {
		case "human": return "human";
		case "system": return "system";
		default: return "agent";
	}
}
function normalizeLegacyProfileIdSuffix(profileId) {
	const normalized = profileId.trim().toLowerCase().replace(PROFILE_ID_SUFFIX_SANITIZE_REGEX, "");
	if (normalized.length === 0) return "";
	const withoutAgentPrefix = normalized.startsWith("agt") ? normalized.slice(3) : normalized;
	return (withoutAgentPrefix.length > 0 ? withoutAgentPrefix : normalized).slice(0, LEGACY_MENTION_ALIAS_PROFILE_ID_SUFFIX_LENGTH);
}
function buildLegacyDefaultMentionAliasSeed(input) {
	const normalized = input.profileName.trim().toLowerCase().replace(LEGACY_HANDLE_SEED_SANITIZE_REGEX, "-").replace(LEGACY_HANDLE_SEED_MULTI_SEPARATOR_REGEX, "-").replace(LEGACY_HANDLE_SEED_EDGE_SEPARATOR_REGEX, "").slice(0, LEGACY_MENTION_ALIAS_SEED_MAX_LENGTH);
	if (normalized.length === 0) return resolveProfileHandleFallbackBase(input.profileKind);
	if (LEGACY_HANDLE_SEED_START_CHAR_REGEX.test(normalized)) return normalized;
	return `agent-${normalized}`.slice(0, LEGACY_MENTION_ALIAS_SEED_MAX_LENGTH);
}
//#endregion
export { DAEMON_STREAM_KEEPALIVE_PING, DAEMON_STREAM_KEEPALIVE_PONG, MAX_MENTION_TOKEN_LENGTH, MAX_PROFILE_ID_LENGTH, MAX_PROFILE_NAME_LENGTH, MAX_TEXT_LENGTH, MENTION_TOKEN_REGEX, SPACE_STREAM_KEEPALIVE_PING, SPACE_STREAM_KEEPALIVE_PONG, buildLegacyDefaultMentionAliasCandidates, buildProfileDisplay, buildProfileHandleBase, buildProfileHandleCandidate, buildProfileHandleCandidates, collectMessageLinks, collectMessagePreviewUrls, isLegacyDefaultMentionAlias, normalizeMentionToken, normalizeMessageLink, normalizeProfileId, parseEnvelope, parseHello, parseIncoming, parseJsonObject, parseProfile, parseServerErrorFrame, parseSignalText, parseSpaceCreator, resolveAvailableProfileHandle, resolveCustomMentionAliasOverride };

//# sourceMappingURL=index.js.map